import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Pencil,
  RotateCcw,
  Search,
  Trash2,
  User,
  X,
} from "lucide-react";
import { supabase } from "../../../../Supabase/SupabaseClient";
import "./AdminReservas.css";

const HORAS = Array.from({ length: 10 }, (_, i) => `${(i + 9).toString().padStart(2, "0")}:00`);

const ESTADOS = [
  { valor: "pendiente", etiqueta: "Pendiente" },
  { valor: "confirmada", etiqueta: "Confirmada" },
  { valor: "completada", etiqueta: "Completada" },
  { valor: "cancelada", etiqueta: "Cancelada" },
];

const POR_PAGINA = 12;

const CAMPOS_EDITABLES = [
  "estado",
  "dia",
  "horario",
  "nombre",
  "email",
  "telefono",
  "tipo_sesion",
  "metodo_pago",
  "presupuesto",
  "direccion",
  "experiencia_anterior",
  "preferencias",
  "mensaje",
  "observaciones",
];

function formatearFecha(fecha) {
  if (!fecha) return "—";
  try {
    return new Date(`${fecha}T00:00:00`).toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return fecha;
  }
}

function formatearFechaCorta(fecha) {
  if (!fecha) return "—";
  try {
    return new Date(`${fecha}T00:00:00`).toLocaleDateString("es-AR", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  } catch {
    return fecha;
  }
}

function formatearMarcaTiempo(valor) {
  if (!valor) return "—";
  try {
    return new Date(valor).toLocaleString("es-AR");
  } catch {
    return valor;
  }
}

const Reservas = ({ standalone = false }) => {
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroServicio, setFiltroServicio] = useState("");
  const [filtroFecha, setFiltroFecha] = useState("");
  const [ordenAsc, setOrdenAsc] = useState(false);
  const [visibles, setVisibles] = useState(POR_PAGINA);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [reservaEditando, setReservaEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [ocupadoId, setOcupadoId] = useState(null);

  const hayFiltros = Boolean(busqueda || filtroEstado || filtroServicio || filtroFecha);

  const cargar = useCallback(async () => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) throw new Error("Acceso denegado");

    const { data, error: fetchError } = await supabase
      .from("reservas")
      .select(`
        id, usuario_id, servicio_id, dia, horario, created_at, updated_at,
        email, telefono, tipo_sesion, preferencias, experiencia_anterior,
        presupuesto, mensaje, estado, nombre, metodo_pago, direccion, observaciones,
        servicios(nombre, descripcion),
        usuarios(nombre, email)
      `)
      .order("created_at", { ascending: false });

    if (fetchError) throw fetchError;
    setReservas(data || []);
  }, []);

  useEffect(() => {
    let activo = true;

    const init = async () => {
      try {
        await cargar();
      } catch (err) {
        if (activo) setError(err.message || String(err));
      } finally {
        if (activo) setLoading(false);
      }
    };

    init();
    return () => {
      activo = false;
    };
  }, [cargar]);

  useEffect(() => {
    setVisibles(POR_PAGINA);
  }, [busqueda, filtroEstado, filtroServicio, filtroFecha, ordenAsc]);

  // Escape cierra el modal
  useEffect(() => {
    if (!modalAbierto) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setModalAbierto(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalAbierto]);

  const serviciosUnicos = useMemo(
    () =>
      [...new Set(reservas.map((r) => r.servicios?.nombre).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [reservas]
  );

  const filtradas = useMemo(() => {
    const term = busqueda.trim().toLowerCase();

    const resultado = reservas.filter((r) => {
      if (filtroEstado && r.estado !== filtroEstado) return false;
      if (filtroServicio && r.servicios?.nombre !== filtroServicio) return false;
      if (filtroFecha && r.dia !== filtroFecha) return false;

      if (!term) return true;

      const campos = [
        r.nombre,
        r.email,
        r.telefono,
        r.servicios?.nombre,
        r.tipo_sesion,
        r.mensaje,
        r.presupuesto,
        r.usuarios?.nombre,
        r.usuarios?.email,
        String(r.id),
      ];

      return campos.some((valor) =>
        String(valor ?? "").toLowerCase().includes(term)
      );
    });

    return resultado.sort((a, b) => {
      const clave = (r) => `${r.dia || ""} ${r.horario || ""}`;
      const cmp = clave(a).localeCompare(clave(b));
      return ordenAsc ? cmp : -cmp;
    });
  }, [reservas, busqueda, filtroEstado, filtroServicio, filtroFecha, ordenAsc]);

  const stats = useMemo(() => {
    const hoy = new Date().toISOString().slice(0, 10);
    const cuenta = (valor) => reservas.filter((r) => r.estado === valor).length;
    return {
      total: reservas.length,
      pendiente: cuenta("pendiente"),
      confirmada: cuenta("confirmada"),
      completada: cuenta("completada"),
      hoy: reservas.filter((r) => r.dia === hoy).length,
    };
  }, [reservas]);

  const resetFiltros = () => {
    setBusqueda("");
    setFiltroEstado("");
    setFiltroServicio("");
    setFiltroFecha("");
  };

  const abrirModal = (reserva) => {
    setReservaEditando({ ...reserva });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setReservaEditando(null);
  };

  const patchReserva = (id, cambios) => {
    setReservas((prev) => prev.map((r) => (r.id === id ? { ...r, ...cambios } : r)));
  };

  const cambiarEstado = async (reserva, estado) => {
    if (!estado || estado === reserva.estado) return;

    setOcupadoId(reserva.id);
    setError(null);
    setFlash(null);
    const anterior = reserva.estado;

    patchReserva(reserva.id, { estado });

    try {
      const { error: updateError } = await supabase
        .from("reservas")
        .update({ estado })
        .eq("id", reserva.id);
      if (updateError) throw updateError;

      setFlash({
        tipo: "ok",
        texto: `Reserva #${reserva.id} pasó a "${estado}".`,
      });
    } catch (err) {
      patchReserva(reserva.id, { estado: anterior });
      setError(err.message || "No se pudo actualizar el estado.");
    } finally {
      setOcupadoId(null);
    }
  };

  const guardarCambios = async () => {
    if (!reservaEditando?.id) return;

    setGuardando(true);
    setError(null);

    try {
      const payload = CAMPOS_EDITABLES.reduce((acc, campo) => {
        acc[campo] = reservaEditando[campo] || null;
        return acc;
      }, {});

      const { error: updateError } = await supabase
        .from("reservas")
        .update(payload)
        .eq("id", reservaEditando.id);
      if (updateError) throw updateError;

      patchReserva(reservaEditando.id, reservaEditando);
      cerrarModal();
      setFlash({ tipo: "ok", texto: `Reserva #${reservaEditando.id} actualizada.` });
    } catch (err) {
      setError(err.message || "No se pudo guardar la reserva.");
    } finally {
      setGuardando(false);
    }
  };

  const eliminarReserva = async (reserva) => {
    const ok = window.confirm(
      `¿Eliminar la reserva #${reserva.id}${
        reserva.nombre ? ` de ${reserva.nombre}` : ""
      }? Esta acción no se puede deshacer.`
    );
    if (!ok) return;

    setOcupadoId(reserva.id);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from("reservas")
        .delete()
        .eq("id", reserva.id);
      if (deleteError) throw deleteError;

      setReservas((prev) => prev.filter((r) => r.id !== reserva.id));
      setFlash({ tipo: "ok", texto: `Reserva #${reserva.id} eliminada.` });
    } catch (err) {
      setError(err.message || "No se pudo eliminar la reserva.");
    } finally {
      setOcupadoId(null);
    }
  };

  if (loading) {
    return (
      <div className={standalone ? "admin-reservas admin-reservas--standalone" : "admin-reservas"}>
        <div className="admin-reservas__loading">
          <Loader2 size={20} aria-hidden="true" />
          Cargando reservas…
        </div>
      </div>
    );
  }

  const visiblesAhora = filtradas.slice(0, visibles);
  const quedan = filtradas.length - visiblesAhora.length;

  return (
    <div className={standalone ? "admin-reservas admin-reservas--standalone" : "admin-reservas"}>
      <div className="admin-reservas__container">
        <div className="admin-reservas__content">
          {/* Resumen */}
          <div className="admin-reservas__stats">
            <div className="admin-reservas__stat">
              <span className="admin-reservas__statNum">{stats.total}</span>
              <span className="admin-reservas__statLabel">Total</span>
            </div>
            <div className="admin-reservas__stat admin-reservas__stat--pendiente">
              <span className="admin-reservas__statNum">{stats.pendiente}</span>
              <span className="admin-reservas__statLabel">Pendientes</span>
            </div>
            <div className="admin-reservas__stat admin-reservas__stat--confirmada">
              <span className="admin-reservas__statNum">{stats.confirmada}</span>
              <span className="admin-reservas__statLabel">Confirmadas</span>
            </div>
            <div className="admin-reservas__stat admin-reservas__stat--completada">
              <span className="admin-reservas__statNum">{stats.completada}</span>
              <span className="admin-reservas__statLabel">Completadas</span>
            </div>
            <div className="admin-reservas__stat admin-reservas__stat--hoy">
              <span className="admin-reservas__statNum">{stats.hoy}</span>
              <span className="admin-reservas__statLabel">Para hoy</span>
            </div>
          </div>

          {/* Filtros */}
          <div className="admin-reservas__filters">
            <div className="admin-reservas__searchWrap">
              <Search size={16} className="admin-reservas__searchIcon" aria-hidden="true" />
              <input
                type="search"
                className="admin-reservas__search"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, email, teléfono, servicio o mensaje…"
                aria-label="Buscar reservas"
              />
            </div>

            <div className="admin-reservas__filters-grid">
              <div className="admin-reservas__filter-group">
                <label className="admin-reservas__filter-label">Estado</label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className="admin-reservas__filter-select"
                >
                  <option value="">Todos</option>
                  {ESTADOS.map((e) => (
                    <option key={e.valor} value={e.valor}>
                      {e.etiqueta}
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-reservas__filter-group">
                <label className="admin-reservas__filter-label">Servicio</label>
                <select
                  value={filtroServicio}
                  onChange={(e) => setFiltroServicio(e.target.value)}
                  className="admin-reservas__filter-select"
                >
                  <option value="">Todos</option>
                  {serviciosUnicos.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-reservas__filter-group">
                <label className="admin-reservas__filter-label">Fecha</label>
                <input
                  type="date"
                  value={filtroFecha}
                  onChange={(e) => setFiltroFecha(e.target.value)}
                  className="admin-reservas__filter-input"
                />
              </div>

              <div className="admin-reservas__filter-group admin-reservas__filter-group--actions">
                <label className="admin-reservas__filter-label">Orden</label>
                <div className="admin-reservas__filter-actions">
                  <button
                    type="button"
                    className="admin-reservas__sortBtn"
                    onClick={() => setOrdenAsc((v) => !v)}
                    title={ordenAsc ? "Ordenar por fecha más reciente" : "Ordenar por fecha más próxima"}
                  >
                    {ordenAsc ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronUp size={15} aria-hidden="true" />}
                    {ordenAsc ? "Recientes" : "Próximas"}
                  </button>
                  {hayFiltros && (
                    <button
                      type="button"
                      className="admin-reservas__reset-btn"
                      onClick={resetFiltros}
                    >
                      <RotateCcw size={14} aria-hidden="true" />
                      Limpiar
                    </button>
                  )}
                </div>
              </div>
            </div>

            <p className="admin-reservas__count">
              {filtradas.length} de {reservas.length} reservas
              {hayFiltros && " (filtradas)"}
            </p>
          </div>

          {error && (
            <div className="admin-reservas__error" role="alert">
              <AlertTriangle size={18} aria-hidden="true" />
              <p>{error}</p>
            </div>
          )}

          {flash && (
            <div className="admin-reservas__success" role="status">
              <Check size={18} aria-hidden="true" />
              <p>{flash.texto}</p>
              <button
                type="button"
                className="admin-reservas__successClose"
                onClick={() => setFlash(null)}
                aria-label="Cerrar aviso"
              >
                <X size={15} aria-hidden="true" />
              </button>
            </div>
          )}

          {visiblesAhora.length === 0 ? (
            <div className="admin-reservas__no-results">
              {hayFiltros
                ? "No hay reservas que coincidan con los filtros aplicados."
                : "Todavía no hay reservas cargadas."}
            </div>
          ) : (
            <>
              {/* Tabla (desktop) */}
              <div className="admin-reservas__table-container">
                <table className="admin-reservas__table">
                  <thead>
                    <tr>
                      <th>Estado</th>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th>Servicio</th>
                      <th>Presupuesto</th>
                      <th>Recibida</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblesAhora.map((r) => {
                      const busy = ocupadoId === r.id;
                      return (
                        <tr key={r.id} className={busy ? "admin-reservas__row--busy" : ""}>
                          <td>
                            <select
                              value={r.estado || ""}
                              onChange={(e) => cambiarEstado(r, e.target.value)}
                              disabled={busy}
                              aria-label={`Estado de la reserva ${r.id}`}
                              className={`admin-reservas__statusSelect ${
                                r.estado ? `admin-reservas__statusSelect--${r.estado}` : ""
                              }`}
                            >
                              <option value="">Sin estado</option>
                              {ESTADOS.map((e) => (
                                <option key={e.valor} value={e.valor}>
                                  {e.etiqueta}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="admin-reservas__cell--date">
                            <strong>{formatearFechaCorta(r.dia)}</strong>
                            <span className="admin-reservas__cellSub">{r.horario?.slice(0, 5) || "—"}</span>
                          </td>
                          <td>
                            <strong className="admin-reservas__cellName">
                              {r.nombre || r.usuarios?.nombre || "—"}
                            </strong>
                            <span className="admin-reservas__cellSub">{r.email || r.usuarios?.email || "—"}</span>
                            <span className="admin-reservas__cellSub">{r.telefono || "—"}</span>
                          </td>
                          <td>
                            {r.servicios?.nombre || "—"}
                            {r.tipo_sesion && (
                              <span className="admin-reservas__cellSub">{r.tipo_sesion}</span>
                            )}
                          </td>
                          <td>{r.presupuesto || "—"}</td>
                          <td className="admin-reservas__cell--meta">
                            {formatearMarcaTiempo(r.created_at)}
                          </td>
                          <td>
                            <div className="admin-reservas__actions-row">
                              <button
                                type="button"
                                className="admin-reservas__iconBtn"
                                onClick={() => abrirModal(r)}
                                disabled={busy}
                                title="Ver y editar"
                              >
                                <Pencil size={15} aria-hidden="true" />
                                <span className="admin-reservas__srOnly">Editar reserva {r.id}</span>
                              </button>
                              <button
                                type="button"
                                className="admin-reservas__iconBtn admin-reservas__iconBtn--danger"
                                onClick={() => eliminarReserva(r)}
                                disabled={busy}
                                title="Eliminar"
                              >
                                <Trash2 size={15} aria-hidden="true" />
                                <span className="admin-reservas__srOnly">Eliminar reserva {r.id}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Cards (móvil) */}
              <ul className="admin-reservas__cards">
                {visiblesAhora.map((r) => {
                  const busy = ocupadoId === r.id;
                  return (
                    <li key={r.id} className="admin-reservas__card">
                      <div className="admin-reservas__cardTop">
                        <span className={`admin-reservas__chip ${
                          r.estado ? `admin-reservas__chip--${r.estado}` : ""
                        }`}>
                          {ESTADOS.find((e) => e.valor === r.estado)?.etiqueta || "Sin estado"}
                        </span>
                        <span className="admin-reservas__cardId">#{r.id}</span>
                      </div>

                      <p className="admin-reservas__cardTitle">
                        {r.nombre || r.usuarios?.nombre || "Sin nombre"}
                      </p>
                      <p className="admin-reservas__cardMeta">
                        {r.email || r.usuarios?.email || "—"} · {r.telefono || "—"}
                      </p>
                      <p className="admin-reservas__cardMeta">
                        <CalendarDays size={13} aria-hidden="true" />
                        {formatearFecha(r.dia)} {r.horario?.slice(0, 5) || ""}
                      </p>
                      <p className="admin-reservas__cardMeta">
                        <User size={13} aria-hidden="true" />
                        {r.servicios?.nombre || "Sin servicio"}
                        {r.presupuesto ? ` · ${r.presupuesto}` : ""}
                      </p>

                      <div className="admin-reservas__cardActions">
                        <select
                          value={r.estado || ""}
                          onChange={(e) => cambiarEstado(r, e.target.value)}
                          disabled={busy}
                          aria-label={`Estado de la reserva ${r.id}`}
                          className={`admin-reservas__statusSelect ${
                            r.estado ? `admin-reservas__statusSelect--${r.estado}` : ""
                          }`}
                        >
                          <option value="">Sin estado</option>
                          {ESTADOS.map((e) => (
                            <option key={e.valor} value={e.valor}>
                              {e.etiqueta}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="admin-reservas__cardBtn"
                          onClick={() => abrirModal(r)}
                          disabled={busy}
                        >
                          <Pencil size={14} aria-hidden="true" />
                          Editar
                        </button>
                        <button
                          type="button"
                          className="admin-reservas__cardBtn admin-reservas__cardBtn--danger"
                          onClick={() => eliminarReserva(r)}
                          disabled={busy}
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {quedan > 0 && (
                <div className="admin-reservas__moreWrap">
                  <button
                    type="button"
                    className="admin-reservas__moreBtn"
                    onClick={() => setVisibles((v) => v + POR_PAGINA)}
                  >
                    Ver {Math.min(quedan, POR_PAGINA)} más
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de edición */}
      {modalAbierto && reservaEditando && (
        <div
          className="admin-reservas__modal-overlay"
          onClick={(e) => e.target === e.currentTarget && cerrarModal()}
        >
          <div className="admin-reservas__modal" role="dialog" aria-modal="true" aria-label={`Reserva ${reservaEditando.id}`}>
            <div className="admin-reservas__modal-header">
              <div>
                <h3 className="admin-reservas__modal-title">Reserva #{reservaEditando.id}</h3>
                <p className="admin-reservas__modal-sub">
                  Recibida el {formatearMarcaTiempo(reservaEditando.created_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={cerrarModal}
                className="admin-reservas__modal-close"
                aria-label="Cerrar"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="admin-reservas__modal-body">
              <div className="admin-reservas__form">
                <div className="admin-reservas__form-group">
                  <label className="admin-reservas__form-label" htmlFor="res-estado">Estado</label>
                  <select
                    id="res-estado"
                    value={reservaEditando.estado || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, estado: e.target.value })}
                    className="admin-reservas__form-select"
                  >
                    <option value="">Sin estado</option>
                    {ESTADOS.map((e) => (
                      <option key={e.valor} value={e.valor}>{e.etiqueta}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-reservas__form-group">
                  <label className="admin-reservas__form-label" htmlFor="res-fecha">Fecha</label>
                  <input
                    id="res-fecha"
                    type="date"
                    value={reservaEditando.dia || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, dia: e.target.value })}
                    className="admin-reservas__form-input"
                  />
                </div>

                <div className="admin-reservas__form-group">
                  <label className="admin-reservas__form-label" htmlFor="res-hora">Horario</label>
                  <select
                    id="res-hora"
                    value={reservaEditando.horario || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, horario: e.target.value })}
                    className="admin-reservas__form-select"
                  >
                    <option value="">Sin horario</option>
                    {HORAS.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-reservas__form-group">
                  <label className="admin-reservas__form-label" htmlFor="res-tipo">Tipo de sesión</label>
                  <input
                    id="res-tipo"
                    type="text"
                    value={reservaEditando.tipo_sesion || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, tipo_sesion: e.target.value })}
                    className="admin-reservas__form-input"
                    placeholder="Ej: Exterior"
                  />
                </div>

                <div className="admin-reservas__form-group admin-reservas__form-group--full">
                  <label className="admin-reservas__form-label" htmlFor="res-nombre">Nombre del cliente</label>
                  <input
                    id="res-nombre"
                    type="text"
                    value={reservaEditando.nombre || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, nombre: e.target.value })}
                    className="admin-reservas__form-input"
                    placeholder="Nombre completo"
                  />
                </div>

                <div className="admin-reservas__form-group">
                  <label className="admin-reservas__form-label" htmlFor="res-email">Email</label>
                  <input
                    id="res-email"
                    type="email"
                    value={reservaEditando.email || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, email: e.target.value })}
                    className="admin-reservas__form-input"
                    placeholder="email@ejemplo.com"
                  />
                </div>

                <div className="admin-reservas__form-group">
                  <label className="admin-reservas__form-label" htmlFor="res-tel">Teléfono</label>
                  <input
                    id="res-tel"
                    type="tel"
                    value={reservaEditando.telefono || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, telefono: e.target.value })}
                    className="admin-reservas__form-input"
                    placeholder="+54 9 ..."
                  />
                </div>

                <div className="admin-reservas__form-group">
                  <label className="admin-reservas__form-label" htmlFor="res-pago">Método de pago</label>
                  <input
                    id="res-pago"
                    type="text"
                    value={reservaEditando.metodo_pago || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, metodo_pago: e.target.value })}
                    className="admin-reservas__form-input"
                    placeholder="Transferencia / efectivo"
                  />
                </div>

                <div className="admin-reservas__form-group">
                  <label className="admin-reservas__form-label" htmlFor="res-presupuesto">Presupuesto</label>
                  <input
                    id="res-presupuesto"
                    type="text"
                    value={reservaEditando.presupuesto || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, presupuesto: e.target.value })}
                    className="admin-reservas__form-input"
                    placeholder="Rango acordado"
                  />
                </div>

                <div className="admin-reservas__form-group admin-reservas__form-group--full">
                  <label className="admin-reservas__form-label" htmlFor="res-direccion">Dirección</label>
                  <input
                    id="res-direccion"
                    type="text"
                    value={reservaEditando.direccion || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, direccion: e.target.value })}
                    className="admin-reservas__form-input"
                    placeholder="Dirección de la sesión"
                  />
                </div>

                <div className="admin-reservas__form-group admin-reservas__form-group--full">
                  <label className="admin-reservas__form-label" htmlFor="res-mensaje">Mensaje del cliente</label>
                  <textarea
                    id="res-mensaje"
                    value={reservaEditando.mensaje || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, mensaje: e.target.value })}
                    className="admin-reservas__form-textarea"
                    placeholder="Lo que pidió el cliente…"
                    rows={3}
                  />
                </div>

                <div className="admin-reservas__form-group admin-reservas__form-group--full">
                  <label className="admin-reservas__form-label" htmlFor="res-preferencias">Preferencias</label>
                  <textarea
                    id="res-preferencias"
                    value={reservaEditando.preferencias || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, preferencias: e.target.value })}
                    className="admin-reservas__form-textarea"
                    placeholder="Preferencias de la sesión…"
                    rows={2}
                  />
                </div>

                <div className="admin-reservas__form-group admin-reservas__form-group--full">
                  <label className="admin-reservas__form-label" htmlFor="res-experiencia">Experiencia anterior</label>
                  <textarea
                    id="res-experiencia"
                    value={reservaEditando.experiencia_anterior || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, experiencia_anterior: e.target.value })}
                    className="admin-reservas__form-textarea"
                    placeholder="Trabajos previos del cliente…"
                    rows={2}
                  />
                </div>

                <div className="admin-reservas__form-group admin-reservas__form-group--full">
                  <label className="admin-reservas__form-label" htmlFor="res-observaciones">Observaciones internas</label>
                  <textarea
                    id="res-observaciones"
                    value={reservaEditando.observaciones || ""}
                    onChange={(e) => setReservaEditando({ ...reservaEditando, observaciones: e.target.value })}
                    className="admin-reservas__form-textarea"
                    placeholder="Notas internas (no las ve el cliente)…"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="admin-reservas__modal-actions">
              <button
                type="button"
                onClick={() => eliminarReserva(reservaEditando)}
                className="admin-reservas__modal-btn admin-reservas__modal-btn--danger"
                disabled={guardando}
              >
                <Trash2 size={15} aria-hidden="true" />
                Eliminar
              </button>
              <div className="admin-reservas__modal-actionsRight">
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="admin-reservas__modal-btn admin-reservas__modal-btn--cancel"
                  disabled={guardando}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={guardarCambios}
                  className="admin-reservas__modal-btn admin-reservas__modal-btn--save"
                  disabled={guardando}
                >
                  {guardando ? (
                    <Loader2 size={15} className="admin-reservas__spin" aria-hidden="true" />
                  ) : (
                    <Check size={15} aria-hidden="true" />
                  )}
                  {guardando ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reservas;